import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Grid, InputBase, Button, Stack } from '@mui/material';
import { MarkEmailReadOutlined, ChevronRight } from '@mui/icons-material';
import { colors, radii, typography } from '../../styles/tokens';
import { fetchLatest, fetchAlerts, fetchArticles, fetchTopics, ArticleListItem } from '../../services/articlesApi';
import { subscribeNewsletter } from '../../services/newsletterApi';
import { toolNavItems } from '../Navigation/toolNavItems';
import { trackEvent } from '../../services/analytics';
import { resolvePageMeta } from '../../services/pageMeta';

const PLACEMENT = 'footer';

const StatCounter: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Box>
    <Typography
      variant="h4"
      sx={{ color: '#FDF3EC', lineHeight: 1.1, fontFamily: typography.fontFamilyDisplay }}
    >
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: '#F3C4AC', fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

// "The Border Brief" — mounted once in Layout so it sits in the same spot
// (last box before the footer) on every route. The nav's "Get the briefing"
// link scrolls to this box's id="newsletter" instead of navigating away.
export const NewsletterCta: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const startedRef = useRef(false);
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — must stay empty
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [latest, setLatest] = useState<ArticleListItem[]>([]);
  const [alertCount, setAlertCount] = useState(0);
  const [totalArticles, setTotalArticles] = useState<number | null>(null);
  const [topicCount, setTopicCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchLatest(4),
      fetchAlerts(6).then((r) => r.articles.length).catch(() => 0),
      fetchArticles({ limit: 1 }).then((r) => r.total).catch(() => null),
      fetchTopics().then((r) => r.topics.length).catch(() => null),
    ])
      .then(([l, alerts, total, topics]) => {
        if (cancelled) return;
        setLatest(l.articles);
        setAlertCount(alerts);
        setTotalArticles(total);
        setTopicCount(topics);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const hasIntel = latest.length > 0;

  // Stats only render when they trace to a real count (product-honesty rule).
  const stats: { value: number; label: string }[] = [
    { value: toolNavItems.length, label: 'Live tools' },
    ...(totalArticles && totalArticles > 0 ? [{ value: totalArticles, label: 'Intelligence stories' }] : []),
    ...(topicCount && topicCount > 0 ? [{ value: topicCount, label: 'Topics covered' }] : []),
    ...(alertCount > 0 ? [{ value: alertCount, label: 'Active market alerts' }] : []),
  ];

  // The box renders on every route, so the page it converted on is the whole
  // point of the event — without it every signup looks identical.
  const { pageType } = resolvePageMeta(pathname);

  const handleFocus = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent('newsletter_start', { placement: PLACEMENT, page_type: pageType });
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (subscribing) return;
    setSubscribing(true);
    setSubscribeError(null);
    try {
      // Same friendly success whether the address is new or already subscribed —
      // the backend never reveals which (no enumeration), so neither do we.
      await subscribeNewsletter({ email, website });
      setSubscribed(true);
      // Counts submissions, not unique subscribers: the backend can't tell us
      // which this was. Postgres stays the source of truth for the list size.
      // The address itself must never appear in an analytics payload.
      trackEvent('newsletter_subscribe', { placement: PLACEMENT, page_type: pageType });
    } catch {
      setSubscribeError('Something went wrong. Please try again.');
      trackEvent('newsletter_error', { placement: PLACEMENT, error_type: 'request_failed' });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <Box
      component="section"
      id="newsletter"
      sx={{
        borderRadius: `${radii.card}px`,
        bgcolor: colors.primary,
        p: { xs: 3, md: 5 },
        mt: 4,
        mb: 4,
        scrollMarginTop: 96,
      }}
    >
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography
            variant="overline"
            component="p"
            sx={{ color: colors.mustard, fontWeight: 600, letterSpacing: '0.12em', mb: 1 }}
          >
            This week in global commerce
          </Typography>
          <Typography
            variant="h4"
            component="h2"
            sx={{ color: '#FDF3EC', mb: 1, fontSize: { xs: '1.5rem', md: '1.75rem' } }}
          >
            The Border Brief - Friday mornings
          </Typography>
          <Typography variant="body2" sx={{ color: '#F3C4AC', maxWidth: 460, lineHeight: 1.6, mb: hasIntel ? 2.5 : 0 }}>
            One email a week: the regulatory changes, FX moves, and market shifts that affect
            cross-border sellers, summarized in a 5-minute read.
          </Typography>

          {/* Digest bullet list — real headlines from the latest articles */}
          {hasIntel && (
            <Stack component="ul" spacing={1.25} sx={{ listStyle: 'none', p: 0, m: 0, mb: 3 }}>
              {latest.slice(0, 4).map((a) => (
                <Box
                  key={a.id}
                  component="li"
                  onClick={() => navigate(`/news/${a.slug}`)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover .digest-title': { color: colors.mustard },
                  }}
                >
                  <ChevronRight sx={{ color: colors.mustard, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
                  <Typography
                    className="digest-title"
                    variant="body2"
                    sx={{ color: '#FDF3EC', fontWeight: 500, lineHeight: 1.45, transition: 'color 150ms ease' }}
                  >
                    {a.title}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          {/* Stat counters — only metrics that trace to real data */}
          {stats.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${stats.length}, auto)` },
                gap: { xs: 2, sm: 4 },
                pt: 1,
              }}
            >
              {stats.map((s) => (
                <StatCounter key={s.label} value={s.value} label={s.label} />
              ))}
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', alignItems: 'center' }}>
          {subscribed ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                bgcolor: colors.paper,
                borderRadius: `${radii.card}px`,
                p: 2,
              }}
            >
              <MarkEmailReadOutlined sx={{ color: colors.accent }} />
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Thanks! The digest is launching soon - we'll be in touch when the first issue ships.
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubscribe} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, width: '100%' }}>
                <InputBase
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={handleFocus}
                  required
                  type="email"
                  placeholder="you@company.com"
                  disabled={subscribing}
                  sx={{
                    flex: 1,
                    bgcolor: colors.paper,
                    borderRadius: `${radii.badge}px`,
                    px: 2.5,
                    py: 1.25,
                    fontSize: '0.9375rem',
                  }}
                />
                {/* Honeypot: hidden from real users; bots that fill it are silently dropped. */}
                <InputBase
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  name="website"
                  inputProps={{ tabIndex: -1, 'aria-hidden': true, autoComplete: 'off' }}
                  sx={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={subscribing}
                  sx={{
                    px: 3,
                    bgcolor: colors.ink,
                    '&:hover': { bgcolor: '#3A3229' },
                  }}
                >
                  {subscribing ? 'Subscribing…' : 'Subscribe'}
                </Button>
              </Box>
              {subscribeError && (
                <Typography variant="caption" sx={{ color: '#FFE1D6', mt: 1, display: 'block' }}>
                  {subscribeError}
                </Typography>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
